<template>
    <div>
        <CompassModal
            ref="importRevertDialog"
            v-model:visible="showDialog"
            size="small"
            class="import-revert-dialog"
            :title="$t.__('settings.import.ImportRevertPopup.cancel_import')"
            @closed="$emit('closed')"
        >
            <template #default>
                <CompassAlert
                    :description="
                        $t.__(
                            'settings.import.ImportRevertPopup.cancellation_and_restoration__data_possible_within_24_hours',
                        )
                    "
                    :showClose="false"
                />
                <p class="description-dialog typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Si vous constatez un problème dans vos données Sellsy depuis que vous avez effectué cet import, annulez le facilement pour restaurer l\’ensemble des données impactées.',
                        )
                    }}
                </p>

                <h6 class="subtitle-card typographies-heading-h4">
                    {{ $l10n.__("Récapitulatif des actions effectuées lors de l'annulation") }}
                </h6>

                <div class="card card-container">
                    <CompassObject
                        v-if="isImportTitleSimple"
                        size="small"
                        :label="typeImport.title"
                        :object="importTypeObject"
                    />
                    <div
                        v-if="typeImport.titleSegments !== undefined"
                        class="compass-object compass-object--small"
                    >
                        <CompassIcon :icon="typeImport.icon" size="medium" />
                        <div class="compass-object__text">
                            <h4>
                                <span
                                    v-for="(segment, idx) in typeImport.titleSegments"
                                    :class="getImportTitleClasses(idx)"
                                >
                                    {{ getImportTitleSegmentText(idx, segment) }}
                                </span>
                            </h4>
                        </div>
                    </div>
                    <div class="card-container__list all-lines-treated">
                        <p>
                            {{ $l10n.__('Annulation des lignes traitées') }}
                        </p>
                        <p class="card-container__infos typographies-paragraph-medium-semibold">
                            {{ importToRevert.processed_rows }}
                        </p>
                    </div>

                    <div class="card-container__categories">
                        <div v-for="(subType, key) in typeImport.displaySubTypeOrder" :key="key">
                            <p class="typographies-paragraph-medium-regular">
                                {{ getResultLabel(subType) }}
                            </p>
                            <div class="card-container__list">
                                <ul>
                                    <li class="typographies-paragraph-medium-regular">
                                        {{ getCancelModificationText() }}
                                    </li>
                                </ul>
                                <p
                                    class="card-container__infos typographies-paragraph-medium-semibold"
                                >
                                    {{ importToRevert.results[subType].updated || 0 }}
                                </p>
                            </div>

                            <div v-if="!isPriceImportType()" class="card-container__list">
                                <ul>
                                    <li class="typographies-paragraph-medium-regular">
                                        {{ $l10n.__('Suppression des ajouts') }}
                                    </li>
                                </ul>

                                <p
                                    class="card-container__infos typographies-paragraph-medium-semibold"
                                >
                                    {{ importToRevert.results[subType].created || 0 }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="import-revert-dialog__footer">
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        @click="showDialog = false"
                    />
                    <CompassButton
                        type="primary-destructive"
                        :label="$t.__('settings.import.ImportRevertPopup.cancel_import')"
                        @click="onRevertImport"
                    />
                </div>
            </template>
        </CompassModal>
    </div>
</template>

<script>
import ImportService from 'api/import/importService';
import ImportTypeDictionary from 'Dictionaries/imports/ImportTypeDictionary';
export default {
    components: {},
    props: {
        importToRevert: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showDialog: false,
            typeImport: {},
        };
    },
    computed: {
        isImportTitleSimple() {
            return (
                this.typeImport.title !== undefined && this.typeImport.titleSegments === undefined
            );
        },
        importTypeObject() {
            return this.typeImport.icon.replace(/obj-slsy-/, '');
        },
    },
    created() {
        this.typeImport = ImportTypeDictionary.find(
            (item) => item.type === this.importToRevert.type,
        );
    },
    mounted() {
        this.showDialog = true;
    },
    methods: {
        onRevertImport() {
            ImportService.revertImport(this.importToRevert.batch_id, this.importToRevert.type)
                .then(() => (this.showDialog = false))
                .catch(() =>
                    this.$pushToast({
                        type: 'error',
                        text: this.$l10n.__(
                            "Une erreur est survenue lors de l'annulation de l'import.",
                        ),
                        close: true,
                        icon: true,
                        unique: true,
                    }),
                );
        },
        getResultLabel(resultKey) {
            switch (resultKey) {
                case 'company':
                    return this.$l10n.__('Sociétés');
                case 'address':
                    return this.$l10n.__('Adresses');
                case 'contact':
                    return this.$l10n.__('Contacts');
                default:
                    return '';
            }
        },
        getImportTitleClasses(idx) {
            return `compass-heading compass-heading--5 ${
                idx < this.typeImport.titleSegments.length - 1 ? 'compass-heading--secondary' : ''
            }`;
        },
        getImportTitleSegmentText(idx, segment) {
            return `${segment}${idx < this.typeImport.titleSegments.length - 1 ? ' > ' : ''}`;
        },
        isPriceImportType() {
            return ['service-price', 'product-price'].includes(this.typeImport.type);
        },
        getCancelModificationText() {
            let text = this.$l10n.__('Annulation des modifications');
            if (this.isPriceImportType()) {
                text = this.$l10n.__('Annulation des modifications des catégories tarifaires');
            }
            return text;
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.import-revert-dialog {
    z-index: 1001;
    &::v-deep {
        p,
        span {
            margin: 0;
        }
    }
    h4,
    h6 {
        margin: 0;
    }
    h6 {
        font-size: var(--font-size-14);
        font-weight: var(--font-weights-proxima-nova-0);
    }
    .description-dialog {
        margin-bottom: var(--spacing-3);
    }
    .subtitle-card {
        margin-bottom: var(--spacing-0);
    }
    .card {
        border-radius: 4px;
        display: flex;
        padding: var(--spacing-3);

        &.card-container {
            background-color: $gray-0;

            .title-container {
                display: flex;
                gap: var(--spacing-1);
                padding-bottom: var(--spacing-1);
                margin-bottom: var(--spacing-1);
                border-bottom: solid 1px var(--colors-common-border-lighter);

                &__title {
                    line-height: 20px;
                    margin: 0;
                }

                &__title-segment {
                    &,
                    &:after {
                        color: var(--colors-common-text-standard-caption);
                    }

                    &:after {
                        content: '>';
                        margin: 0 var(--spacing-0);
                    }

                    &:last-child {
                        color: var(--colors-common-text-standard-default);

                        &:after {
                            content: '';
                        }
                    }
                }
            }
        }
        .card-container {
            &__list {
                align-items: stretch;
                display: flex;
                flex-direction: row;
                height: 20px;
                justify-content: space-between;
                li {
                    list-style-type: disc;
                    margin-left: 20px;
                }

                &.all-lines-treated {
                    border-top: 1px solid var(--colors-common-border-lighter);
                    padding-top: var(--spacing-1);
                    margin-top: var(--spacing-1);
                    margin-bottom: var(--spacing-1);
                }
            }
            &__infos {
                text-align: end;
            }
            &__categories {
                display: flex;
                flex-direction: column;
                gap: var(--spacing-0);
            }
        }
    }

    .import-revert-dialog__footer {
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-end;
        padding: var(--spacing-4) var(--spacing-5) var(--spacing-5);
    }
}
</style>
